import React, {useMemo} from 'react';
import {Box} from 'src/design/atoms/Box';
import type {MRT_ColumnDef} from 'mantine-react-table';
import type {CourseAssignmentPersonPivotItem} from 'src/api/ProactApiClient';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {PersonAssignmentsPanel} from './PersonAssignmentsPanel';

export interface ComplianceByPersonPivotProps {
  rows: CourseAssignmentPersonPivotItem[];
  isLoading: boolean;
}

/**
 * By-person pivot for the Compliance Cockpit: one row per person with
 * course/overdue counts and worst lateness, row-expand to their individual
 * assignments (`sessionUserId` filter on the V2 list endpoint).
 */
export const ComplianceByPersonPivot: React.FC<ComplianceByPersonPivotProps> = ({
  rows,
  isLoading,
}) => {
  const columns = useMemo<MRT_ColumnDef<CourseAssignmentPersonPivotItem>[]>(
    () => [
      {accessorKey: 'userName', header: 'Person'},
      {accessorKey: 'employeeId', header: 'Employee ID'},
      {accessorKey: 'departmentName', header: 'Department'},
      {accessorKey: 'supervisorName', header: 'Supervisor'},
      {accessorKey: 'vendorName', header: 'Vendor'},
      {accessorKey: 'courseCount', header: '# Courses'},
      {
        accessorKey: 'overdueCount',
        header: '# Overdue',
        Cell: ({cell}) => {
          const value = cell.getValue<number>();
          return <Box c={value > 0 ? 'red' : undefined}>{value}</Box>;
        },
      },
      {
        accessorKey: 'worstLatenessDays',
        header: 'Worst Lateness (days)',
        Cell: ({cell}) => cell.getValue<number | undefined>() ?? '',
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'training-compliance-by-person',
    columns,
    defaultDensity: 'xs',
    defaultPagination: {pageIndex: 0, pageSize: 20},
    defaultSorting: [{id: 'overdueCount', desc: true}],
  });

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: false,
      manualFiltering: false,
      manualPagination: false,
      enableRowActions: false,
      columns,
      data: rows,
      getRowId: row => String(row.userId),
      renderDetailPanel: ({row}) => (
        <PersonAssignmentsPanel userId={row.original.userId} />
      ),
    },
    totalCount: rows.length,
    isError: false,
    isLoading,
    isFetching: isLoading,
  });

  return <>{table}</>;
};
